import { DepositApiUrls } from "@/modules/deposit/common/enum";
import type {
  IDepositRequest,
  IDepositScheduleRequest,
  IDepositsRequest,
  IDepositTransactionRequest,
  TDepositResponse,
  TDepositScheduleResponse,
  TDepositsResponse,
  TDepositTransactionResponse,
} from "@/modules/deposit/common/types";
import { ResponseType } from "@/shared/common/enums";
import type {
  IBlobError,
  IBlobUrlResponse,
  IStatementWithIdDownloadRequest,
  IStatementWithIdRequest,
} from "@/shared/common/types";
import { buildRoute, generateQuery } from "@/shared/helpers";
import { blobToText } from "@/shared/utils/common";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "app/axios";

export const depositApi = createApi({
  reducerPath: "depositApi",
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    getDeposits: builder.query<TDepositsResponse, IDepositsRequest>({
      query: ({ pageSize }) => {
        const query = generateQuery({ pageSize });
        const url = `${DepositApiUrls.Deposits}?${query}`;
        return { url };
      },
    }),
    getDepositById: builder.query<TDepositResponse, IDepositRequest>({
      query: ({ id }) => ({
        url: `${DepositApiUrls.Deposits}/${id}`
      })
    }),
    getDepositTransactions: builder.query<
      TDepositTransactionResponse,
      IDepositTransactionRequest
    >({
      query: ({
        id,
        pageIndex,
        pageSize,
        startDate,
        endDate,
      }) => {
        const query = generateQuery({
          pageIndex,
          pageSize,
          startDate,
          endDate,
        });
        const url = buildRoute(
          DepositApiUrls.DepositTransactions,
          { id },
        );
        return { url: `${url}?${query}` };
      },
    }),
    getDepositSchedule: builder.query<
      TDepositScheduleResponse,
      IDepositScheduleRequest
    >({
      query: ({
        id,
        pageIndex,
        pageSize,
      }) => {
        const query = generateQuery({
          pageIndex,
          pageSize,
        });
        const url = buildRoute(DepositApiUrls.DepositSchedule, { id });
        return { url: `${url}?${query}` };
      },
    }),
    getDepositStatement: builder.query<
      string,
      IStatementWithIdRequest
    >({
      query: ({
        id,
        startDate,
        endDate,
      }) => {
        const query = generateQuery({
          startDate,
          endDate,
        });
        const url = buildRoute(DepositApiUrls.DepositStatement, { id });
        return {
          url: `${url}?${query}`,
        };
      }
    }),
    downloadDepositStatement: builder.query<
      IBlobUrlResponse,
      IStatementWithIdDownloadRequest
    >({
      query: ({
        id,
        exportFormat,
        startDate,
        endDate,
      }) => {
        const query = generateQuery({
          exportFormat,
          startDate,
          endDate,
        });
        const url = buildRoute(DepositApiUrls.DepositStatementExport, { id });
        return {
          url: `${url}?${query}`,
          responseType: ResponseType.Blob
        };
      },
      transformResponse: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        return { url };
      },
      transformErrorResponse: async (error: IBlobError) => {
        const text = await blobToText(error) as string;
        const data = text ? JSON.parse(text) : null;

        return {
          status: error.status,
          data
        };
      }
    }),
  }),
});

export const {
  useGetDepositsQuery,
  useGetDepositByIdQuery,
  useGetDepositTransactionsQuery,
  useGetDepositScheduleQuery,
  useGetDepositStatementQuery,
  useLazyDownloadDepositStatementQuery,
} = depositApi;
